/* eslint-disable @typescript-eslint/naming-convention */
import clsx from 'clsx'
import { rem } from 'polished'
import React from 'react'
import { FileError, useDropzone } from 'react-dropzone'
import styled from 'styled-components'

import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from './constants'

import LineCloudUploadIcon from 'components/Luxkit/Icons/line/LineCloudUploadIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'

const DropzoneContainer = styled.div`
  cursor: pointer;
  border: 1px dashed ${({ theme }) => theme.palette.neutral.default.five};
  background: ${({ theme }) => theme.palette.neutral.default.seven};
  padding: ${rem(16)} ${rem(20)};

  &.isDragging {
    background-color: ${({ theme }) => theme.palette.neutral.default.five};
  }
`

const Underline = styled.span`
  text-decoration-line: underline;
`

interface Props {
  onSelectFile: (file: File) => void
  onRejectFile: (error: FileError) => void
  disabled?: boolean
}

function DocumentDropzone({ onSelectFile, onRejectFile, disabled }: Props) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]

        if (/[%?#]/.test(file.name)) {
          onRejectFile({
            message: 'File name contains invalid characters (% ? #)',
            code: 'invalid-file-name',
          })
        } else {
          onSelectFile(file)
        }
      }
    },
    onDropRejected: (rejections) => {
      console.error('Files rejected:', rejections)
      if (rejections[0]) {
        onRejectFile(rejections[0].errors[0])
      }
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
      'image/heic': ['.heic'],
    },
    disabled,
  })

  return (
    <DropzoneContainer
      {...getRootProps()}
      className={clsx({ isDragging: isDragActive })}
    >
      <input {...getInputProps()} />
      <Group direction="vertical" gap={4} fullWidth horizontalAlign="center">
        <LineCloudUploadIcon size="L" />
        <HeadingTextBlock variant="heading4">
          <CSSBreakpoint min="desktop">
            Drag and drop or <Underline>browse</Underline>
          </CSSBreakpoint>
          <CSSBreakpoint max="tablet">Browse files</CSSBreakpoint>
        </HeadingTextBlock>
        <CaptionTextBlock
          variant="medium"
          colour="neutral-three"
          align="center"
        >
          Accepted formats: .PDF, .JPEG, .PNG and .HEIC (max {MAX_FILE_SIZE_MB}
          MB)
        </CaptionTextBlock>
      </Group>
    </DropzoneContainer>
  )
}

export default DocumentDropzone
